import type {
  TesterFeedbackCategory,
  TesterFeedbackContext,
  TesterFeedbackSurface,
} from '@steward-app/steward-types';
import { APP_VERSION } from '../lib/appEnv';
import type { TesterFeedbackScreenState } from './screen';

/**
 * What the tester feedback button sends with a note, and the send itself
 * (crossmarc/steward-app#2531, TEMPORARY).
 *
 * 🚨 WHAT IS NEVER SENT, and `TesterFeedback.test.tsx` pins the answer, query string, fragment and cookie cases: an answer or anything typed into the
 * form, a draft, the query string or fragment (an invitation code travels in the fragment),
 * a token, a cookie (`credentials: 'omit'`), or a picture of the screen. The context is the
 * screen's name, the path, the host, the release, a device class, the window size, the
 * browser's user agent and, on a signed-in screen, the login's address. The page's main
 * heading goes ONLY when no screen named itself: a named screen's heading can hold the
 * client's own name (onboarding's "Welcome, {name}"), which the dialog does not disclose.
 * The dialog's "Sent with your note" line names each of these.
 *
 * Every string is cut to the server's limit and has its control characters replaced here, so a
 * long heading or an odd user agent never turns a tester's note into a 400.
 */

/** A viewport narrower than this is a phone, the same line the funnel beacon draws. */
const PHONE_MAX_WIDTH_PX = 767;

const CONTROL = /[\u0000-\u001F\u007F-\u009F]/gu;
/** The same, less tab and newline, which a note may keep. A pasted `\r\n` becomes `\n`. */
const CONTROL_IN_NOTE = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/gu;

export function cleanNote(text: string): string {
  return text.replace(/\r\n?/g, '\n').replace(CONTROL_IN_NOTE, ' ').trim();
}

/** One line: control characters to spaces, whitespace collapsed, trimmed, cut to `max`. */
export function oneLine(text: string | null | undefined, max: number): string {
  return (text ?? '').replace(CONTROL, ' ').replace(/\s+/g, ' ').trim().slice(0, max).trim();
}

function device(): 'phone' | 'desktop' {
  try {
    return window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH_PX}px)`).matches ? 'phone' : 'desktop';
  } catch {
    return 'desktop';
  }
}

function dimension(n: number): number {
  return Number.isFinite(n) ? Math.min(99_999, Math.max(0, Math.round(n))) : 0;
}

/** Just enough of an address check that the server's `z.email()` is not the first to refuse it. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The page's main heading as shown, ignoring the feedback dialog's own. */
function mainHeading(): string {
  const h1 = Array.from(document.querySelectorAll('h1'))
    .find((el) => !el.closest('[data-tester-feedback]'));
  return oneLine(h1?.textContent, 200);
}

export function collectTesterFeedbackContext(
  surface: TesterFeedbackSurface,
  named: TesterFeedbackScreenState,
): TesterFeedbackContext {
  const email = oneLine(named.email, 254);
  const screen = oneLine(named.screen, 160);
  return {
    surface,
    screen,
    heading: screen ? '' : mainHeading(),
    // `pathname` carries no `?` or `#` by definition; cut, it still starts with `/`.
    path: window.location.pathname.slice(0, 300) || '/',
    host: oneLine(window.location.hostname, 253),
    release: oneLine(APP_VERSION, 80),
    device: device(),
    viewport: `${dimension(window.innerWidth)}x${dimension(window.innerHeight)}`,
    userAgent: oneLine(navigator.userAgent, 400),
    ...(LOOKS_LIKE_EMAIL.test(email) ? { email } : {}),
  };
}

export type TesterFeedbackOutcome = 'sent' | 'invalid' | 'unavailable';

/** Longer than the filer's 20 seconds, shorter than the gateway's 30. */
const SEND_TIMEOUT_MS = 25_000;

export async function sendTesterFeedback(note: {
  message: string;
  category: TesterFeedbackCategory | null;
  firmId: string;
  context: TesterFeedbackContext;
  /** The hidden field a person never sees. Sent only when something filled it. */
  website: string;
}): Promise<TesterFeedbackOutcome> {
  const body = {
    message: cleanNote(note.message),
    ...(note.category ? { category: note.category } : {}),
    firmId: note.firmId,
    context: note.context,
    ...(note.website ? { website: note.website } : {}),
  };
  try {
    const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/tester-feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
      signal: AbortSignal.timeout(SEND_TIMEOUT_MS),
    });
    if (res.status === 202) return 'sent';
    if (res.status === 400) return 'invalid';
    return 'unavailable';
  } catch {
    return 'unavailable';
  }
}
